import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "space-y-1" }
const _hoisted_2 = { class: "flex items-center gap-2.5" }
const _hoisted_3 = { class: "flex flex-wrap gap-2.5" }
const _hoisted_4 = { class: "space-y-3 pt-2" }
const _hoisted_5 = { class: "flex items-center gap-3.5 flex-1 min-w-0" }
const _hoisted_6 = { class: "space-y-2 flex-1 min-w-0 max-w-md" }
const _hoisted_7 = { class: "size-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center mb-3" }
const _hoisted_8 = { key: 2 }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "min-w-0 flex-1 flex items-start gap-4" }
const _hoisted_11 = { class: "min-w-0 flex-1 space-y-1.5" }
const _hoisted_12 = { class: "flex flex-wrap items-center gap-2.5" }
const _hoisted_13 = { class: "font-semibold text-slate-700" }
const _hoisted_14 = { class: "shrink-0 self-start sm:self-center w-full sm:w-auto" }
const _hoisted_15 = {
  class: "size-14 sm:size-16 rounded-full bg-blue-50 border border-blue-100 text-[#1f7aff] flex items-center justify-center mb-3.5 shadow-xs shrink-0",
  "aria-hidden": "true"
}

import { computed, ref, inject } from "vue";
import { useRouter } from "vue-router";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import DMSuiteIcon from "@/components/iconify/DMSuiteIcon.vue";
import { DASHBOARD_ICONS } from "@/config/dashboardIcons";

type CategoryName = (typeof categoriesList)[number];


export default /*@__PURE__*/_defineComponent({
  __name: 'NeedsAttentionPanel',
  props: {
    items: { default: () => [] },
    loading: { type: Boolean, default: false },
    error: { default: null }
  },
  emits: ["action", "retry"],
  setup(__props: any) {

const props = __props;



const router = useRouter();
const comfortMode = inject<any>("comfortMode", ref("comfortable"));
const selectedCategory = ref("All");
const categoriesList = [
  "All",
  "Approvals",
  "Content",
  "Feedback",
  "Revisions",
  "Other",
] as const;

const safeItems = computed(() =>
  Array.isArray(props.items) ? props.items : []
);

const getItemCategory = (item: any): CategoryName => {
  const title = String(item.title || item.subject || "").toLowerCase();
  const status = String(item.status || "").toLowerCase();

  if (
    title.includes("approve") ||
    title.includes("approval") ||
    status.includes("approval")
  ) {
    return "Approvals";
  }
  if (
    title.includes("content") ||
    title.includes("copy") ||
    title.includes("video") ||
    title.includes("image")
  ) {
    return "Content";
  }
  if (
    title.includes("feedback") ||
    title.includes("comment") ||
    title.includes("input")
  ) {
    return "Feedback";
  }
  if (
    title.includes("revision") ||
    title.includes("change") ||
    title.includes("edit")
  ) {
    return "Revisions";
  }
  return "Other";
};

const categoryCounts = computed(() => {
  const counts: Record<CategoryName, number> = {
    All: safeItems.value.length,
    Approvals: 0,
    Content: 0,
    Feedback: 0,
    Revisions: 0,
    Other: 0,
  };

  safeItems.value.forEach((item) => {
    const cat = getItemCategory(item);
    counts[cat]++;
  });

  return counts;
});

const sortedItems = computed(() => {
  return [...safeItems.value];
});

const filteredAndSortedItems = computed(() => {
  let list = sortedItems.value;

  if (selectedCategory.value !== "All") {
    list = list.filter(
      (item) => getItemCategory(item) === selectedCategory.value
    );
  }

  return list;
});

const displayedItems = computed(() => {
  const limit = comfortMode.value === "comfortable" ? 5 : 8;
  return filteredAndSortedItems.value.slice(0, limit);
});

function rowIconForCategory(cat: string): string {
  if (cat === "Approvals") return DASHBOARD_ICONS.sparkle;
  if (cat === "Content") return DASHBOARD_ICONS.promotionalMaterials;
  if (cat === "Feedback") return DASHBOARD_ICONS.socialMedia;
  if (cat === "Revisions") return DASHBOARD_ICONS.refresh;
  return DASHBOARD_ICONS.needsReview;
}

function rowIconColorClass(cat: string) {
  if (cat === "Approvals")
    return "bg-[#e8f1ff] text-[#2855a6] border-[#bfdbfe]/50";
  if (cat === "Content")
    return "bg-[#fdf2f8] text-[#db2777] border-[#fbcfe8]/50";
  if (cat === "Feedback")
    return "bg-[#fff7ed] text-[#f97316] border-[#fed7aa]/50";
  if (cat === "Revisions")
    return "bg-[#f5f3ff] text-[#7c3aed] border-[#ddd6fe]/50";
  return "bg-[#eff6ff] text-[#2563eb] border-[#bfdbfe]/50";
}

const formatDate = (dateStr?: string | Date | null) => {
  if (!dateStr) return "May 28, 2025";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "May 28, 2025";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const itemActionLabel = (item: any): string => {
  const title = String(item?.title || item?.subject || "").toLowerCase();
  if (title.includes("approve") || title.includes("approval")) return "Approve";
  if (title.includes("feedback") || title.includes("comment")) return "Provide Feedback";
  return "Review";
};

const handleViewAll = () => {
  router.push("/apps/task-dashboard");
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["flex min-w-0 w-full flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm text-left", _unref(comfortMode) === 'comfortable' ? 'p-6 md:p-8' : ''])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4", _unref(comfortMode) === 'comfortable' ? 'px-2 pt-2 pb-6' : 'px-6 pt-6'])
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("h2", {
            class: _normalizeClass(
              _unref(cn)(
                'font-black text-slate-900',
                _unref(comfortMode) === 'comfortable'
                  ? 'text-2xl md:text-3xl'
                  : 'text-xl'
              )
            )
          }, " Needs Your Attention ", 2),
          _createElementVNode("span", {
            class: _normalizeClass(["inline-flex items-center justify-center rounded-full text-xs font-black h-5.5 px-2 min-w-5.5 transition-colors", safeItems.value.length > 0 ? 'bg-rose-500 text-white' : 'hidden'])
          }, _toDisplayString(safeItems.value.length), 3)
        ]),
        _createElementVNode("p", {
          class: _normalizeClass(
            _unref(cn)(
              'font-semibold',
              _unref(comfortMode) === 'comfortable'
                ? 'text-sm md:text-base text-slate-600'
                : 'text-xs text-slate-400'
            )
          )
        }, " These items need your review, approval, or feedback. ", 2)
      ]),
      (!__props.loading && !__props.error && safeItems.value.length > 0)
        ? (_openBlock(), _createBlock(_unref(Button), {
            key: 0,
            variant: "link",
            class: _normalizeClass(
          _unref(cn)(
            'font-bold text-[#1f7aff] hover:text-[#185adb] p-0 h-auto self-start sm:self-center',
            _unref(comfortMode) === 'comfortable' ? 'text-sm md:text-base' : 'text-xs'
          )
        ),
            onClick: handleViewAll
          }, {
            default: _withCtx(() => [
              _createTextVNode(" View All (" + _toDisplayString(safeItems.value.length) + ") ", 1)
            ]),
            _: 1
          }, 8, ["class"]))
        : _createCommentVNode("", true)
    ], 2),
    (__props.loading)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["space-y-4", _unref(comfortMode) === 'comfortable' ? 'px-2 pb-2' : 'px-6 pb-6']),
          "data-testid": "attention-loading-state"
        }, [
          _createElementVNode("div", _hoisted_3, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (n) => {
              return _createVNode(_unref(Skeleton), {
                key: `chip-skel-${n}`,
                class: _normalizeClass(
            _unref(cn)(
              'rounded-full bg-slate-100',
              _unref(comfortMode) === 'comfortable' ? 'h-11 w-24' : 'h-8.5 w-20'
            )
          )
              }, null, 8, ["class"])
            }), 64))
          ]),
          _createElementVNode("div", _hoisted_4, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (n) => {
              return _createElementVNode("div", {
                key: `row-skel-${n}`,
                class: "flex items-center justify-between gap-4 p-3 rounded-2xl border border-slate-100/80 bg-slate-50/40"
              }, [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_unref(Skeleton), {
                    class: _normalizeClass(
                _unref(cn)(
                  'rounded-2xl bg-slate-100 shrink-0',
                  _unref(comfortMode) === 'comfortable' ? 'h-13 w-13' : 'h-11 w-11'
                )
              )
                  }, null, 8, ["class"]),
                  _createElementVNode("div", _hoisted_6, [
                    _createVNode(_unref(Skeleton), { class: "h-4 w-3/4 rounded-md bg-slate-100" }),
                    _createVNode(_unref(Skeleton), { class: "h-3 w-1/2 rounded-md bg-slate-100" })
                  ])
                ]),
                _createVNode(_unref(Skeleton), {
                  class: _normalizeClass(
              _unref(cn)(
                'rounded-xl bg-slate-100 shrink-0',
                _unref(comfortMode) === 'comfortable' ? 'h-11 w-24' : 'h-8.5 w-20'
              )
            )
                }, null, 8, ["class"])
              ])
            }), 64))
          ])
        ], 2))
      : (__props.error)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: _normalizeClass(["flex flex-col items-center justify-center text-center rounded-2xl border border-dashed border-rose-200 bg-rose-50/30 p-8 my-2", 
        _unref(comfortMode) === 'comfortable' ? 'mx-2 my-2 py-10' : 'mx-6 my-4 py-8'
      ]),
            "data-testid": "attention-error-state"
          }, [
            _createElementVNode("div", _hoisted_7, [
              _createVNode(DMSuiteIcon, {
                icon: _unref(DASHBOARD_ICONS).warning,
                size: 24,
                "custom-class": "text-rose-600"
              }, null, 8, ["icon"])
            ]),
            _createElementVNode("h3", {
              class: _normalizeClass(
          _unref(cn)(
            'font-extrabold text-slate-800',
            _unref(comfortMode) === 'comfortable' ? 'text-lg' : 'text-base'
          )
        )
            }, " Unable to load attention items ", 2),
            _createElementVNode("p", {
              class: _normalizeClass(
          _unref(cn)(
            'text-slate-500 font-medium max-w-sm mt-1 mb-4',
            _unref(comfortMode) === 'comfortable' ? 'text-sm' : 'text-xs'
          )
        )
            }, _toDisplayString(typeof __props.error === "string"
            ? __props.error
            : __props.error?.message ||
              "Something went wrong while fetching your attention items. Please try again."), 3),
            _createVNode(_unref(Button), {
              size: "sm",
              variant: "outline",
              class: "rounded-xl border-slate-200 bg-white hover:bg-slate-50 font-bold text-slate-700 flex items-center gap-2",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('retry')))
            }, {
              default: _withCtx(() => [
                _createVNode(DMSuiteIcon, {
                  icon: _unref(DASHBOARD_ICONS).refresh,
                  size: 14
                }, null, 8, ["icon"]),
                _cache[1] || (_cache[1] = _createTextVNode(" Try Again ", -1))
              ]),
              _: 1
            })
          ], 2))
        : (safeItems.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createElementVNode("div", {
                class: _normalizeClass(["flex flex-wrap gap-2.5 bg-white", _unref(comfortMode) === 'comfortable' ? 'px-2 pb-6' : 'px-6 pb-4'])
              }, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(categoriesList, (chip) => {
                  return _createElementVNode("button", {
                    key: chip,
                    type: "button",
                    class: _normalizeClass(["inline-flex items-center gap-2 rounded-full font-extrabold transition-all border shrink-0", [
            selectedCategory.value === chip
              ? 'bg-[#1f7aff] text-white border-[#1f7aff] shadow-sm'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900',
            _unref(comfortMode) === 'comfortable'
              ? 'px-5 py-2.5 text-sm h-11'
              : 'px-4 py-1.5 text-xs h-8.5',
          ]]),
                    onClick: ($event: any) => (selectedCategory.value = chip)
                  }, [
                    _createTextVNode(_toDisplayString(chip) + " ", 1),
                    _createElementVNode("span", {
                      class: _normalizeClass(["inline-flex items-center justify-center rounded-full font-black", [
              selectedCategory.value === chip
                ? 'bg-[#185adb] text-white'
                : 'bg-slate-100 text-slate-500',
              _unref(comfortMode) === 'comfortable'
                ? 'text-xs px-2 h-5.5 min-w-5.5'
                : 'text-[10px] px-1.5 min-w-5 h-4.5',
            ]])
                    }, _toDisplayString(categoryCounts.value[chip]), 3)
                  ], 10, _hoisted_9)
                }), 64))
              ], 2),
              (displayedItems.value.length > 0)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(["divide-y divide-slate-100 border-t border-slate-50 bg-white", _unref(comfortMode) === 'comfortable' ? 'px-2' : 'px-6'])
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayedItems.value, (item) => {
                      return (_openBlock(), _createElementBlock("article", {
                        key: item.id,
                        class: _normalizeClass(["flex flex-col sm:flex-row sm:items-center justify-between gap-4", _unref(comfortMode) === 'comfortable' ? 'py-5 md:py-6' : 'py-4'])
                      }, [
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("div", {
                            class: _normalizeClass(["rounded-2xl border flex items-center justify-center shrink-0", [
                rowIconColorClass(getItemCategory(item)),
                _unref(comfortMode) === 'comfortable' ? 'h-13 w-13' : 'h-11 w-11',
              ]])
                          }, [
                            _createVNode(DMSuiteIcon, {
                              icon: rowIconForCategory(getItemCategory(item)),
                              size: _unref(comfortMode) === 'comfortable' ? 26 : 22
                            }, null, 8, ["icon", "size"])
                          ], 2),
                          _createElementVNode("div", _hoisted_11, [
                            _createElementVNode("div", _hoisted_12, [
                              _createElementVNode("h4", {
                                class: _normalizeClass(
                    _unref(cn)(
                      'font-extrabold text-slate-900 leading-snug',
                      _unref(comfortMode) === 'comfortable'
                        ? 'text-base md:text-lg'
                        : 'text-sm sm:text-base'
                    )
                  )
                              }, _toDisplayString(item.title || item.subject), 3)
                            ]),
                            _createElementVNode("div", {
                              class: _normalizeClass(
                  _unref(cn)(
                    'flex items-center gap-x-2 font-medium',
                    _unref(comfortMode) === 'comfortable'
                      ? 'text-sm text-slate-500'
                      : 'text-xs text-slate-400'
                  )
                )
                            }, [
                              _createElementVNode("span", _hoisted_13, _toDisplayString(item.service_name || item.serviceType || "Ad Management"), 1),
                              _cache[2] || (_cache[2] = _createElementVNode("span", { class: "text-slate-300" }, "•", -1)),
                              _createElementVNode("span", null, " Submitted " + _toDisplayString(formatDate(item.created_at || item.due_date)), 1)
                            ], 2)
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_14, [
                          _createVNode(_unref(Button), {
                            size: "sm",
                            variant: "outline",
                            class: _normalizeClass(
                _unref(cn)(
                  'w-full p-2 sm:w-auto rounded-xl border-blue-200 bg-white text-[#1f7aff] hover:bg-blue-50/50 hover:text-[#185adb] transition-all font-bold',
                  _unref(comfortMode) === 'comfortable'
                    ? 'h-11 px-6 text-sm md:text-base'
                    : 'h-8.5 px-5 text-sm'
                )
              ),
                            onClick: ($event: any) => (_ctx.$emit('action', item))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(itemActionLabel(item)), 1)
                            ]),
                            _: 2
                          }, 1032, ["class", "onClick"])
                        ])
                      ], 2))
                    }), 128))
                  ], 2))
                : (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: _normalizeClass(["py-12 text-center border-t border-slate-100 bg-slate-50/20", _unref(comfortMode) === 'comfortable' ? 'px-2' : 'px-6'])
                  }, [
                    _createElementVNode("p", {
                      class: _normalizeClass(
            _unref(cn)(
              'font-semibold text-slate-500',
              _unref(comfortMode) === 'comfortable' ? 'text-base md:text-lg' : 'text-sm'
            )
          )
                    }, " No items found under the selected filter \"" + _toDisplayString(selectedCategory.value) + "\". ", 3)
                  ], 2))
            ]))
          : (_openBlock(), _createElementBlock("div", {
              key: 3,
              class: _normalizeClass(["flex flex-col items-center justify-center text-center rounded-2xl border border-slate-100 bg-slate-50/40", 
        _unref(comfortMode) === 'comfortable'
          ? 'mx-2 mb-2 py-10 sm:py-12 px-6'
          : 'mx-6 mb-6 py-8 px-4'
      ]),
              "data-testid": "attention-empty-state"
            }, [
              _createElementVNode("div", _hoisted_15, [
                _createVNode(DMSuiteIcon, {
                  icon: _unref(DASHBOARD_ICONS).completed,
                  size: 32,
                  "custom-class": "text-[#1f7aff]"
                }, null, 8, ["icon"])
              ]),
              _cache[3] || (_cache[3] = _createElementVNode("div", { class: "mb-3" }, [
                _createElementVNode("span", { class: "inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-emerald-50 border border-emerald-200/70 text-emerald-700 text-xs font-bold" }, [
                  _createElementVNode("span", {
                    class: "size-1.5 rounded-full bg-emerald-500",
                    "aria-hidden": "true"
                  }),
                  _createTextVNode(" All clear ")
                ])
              ], -1)),
              _createElementVNode("h3", {
                class: _normalizeClass(
          _unref(cn)(
            'font-black text-slate-900 tracking-tight',
            _unref(comfortMode) === 'comfortable'
              ? 'text-lg sm:text-xl'
              : 'text-base sm:text-lg'
          )
        )
              }, " You're all caught up! ", 2),
              _createElementVNode("p", {
                class: _normalizeClass(
          _unref(cn)(
            'font-medium text-slate-500 max-w-md mx-auto mt-1.5 leading-relaxed',
            _unref(comfortMode) === 'comfortable'
              ? 'text-sm sm:text-base'
              : 'text-xs sm:text-sm'
          )
        )
              }, " No approvals or feedback needed. Any items requiring your review will appear here. ", 2)
            ], 2))
  ], 2))
}
}

})